import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { EditableDataGrid, GridColumn, ProductSearchResult } from '@/app/pages/sales-invoices/components/EditableDataGrid';
import { SalesInvoiceFormValues, salesInvoiceSchema } from './schema';
import { Site } from '@/app/types/site';
import { Item } from '@/app/types/item';
import { VoucherResponse } from '@/app/types/voucher';
import { Godown } from '@/app/types/inventory';
import { cn } from '@/lib/utils';
import { Decimal } from 'decimal.js';
import { recalculateItemPriceAsync } from '@/app/lib/salePriceRuleUtils';
import { usePreferenceStore } from '@/app/store/preferenceStore';
import { PartySelector } from '@/app/components/common/PartySelector';
import { useFormHotkeys } from '@/app/hooks/useFormHotkeys';
import { getItemPriceDetailsWithFallback, isSQLiteAvailable } from '@/lib/indexeddb/integrationUtils';
import { getItemPricingDetails } from '@/app/lib/invoiceCalculations';
import { getItemByIdAsync } from '@/lib/indexeddb/database';

// Define CalculationItem type at component level
type CalculationItem = { 
    quantity?: number | string | null, 
    rate?: number | string | null, 
    discount_percent?: number | string | null 
};

interface SalesInvoiceFormProps {
  salesInvoice?: VoucherResponse; // For edit mode
  sites: Site[];
  godowns: Godown[]; // Add godowns (warehouses) for selection
  voucherTypeId: number; // Sales Invoice voucher type ID (pre-fetched)
  isSubmitting: boolean;
  onSubmit: (data: SalesInvoiceFormValues) => void;
  searchItems: (query: string) => Promise<Item[]>;
  isLoadingItems: boolean;
  isDisabled?: boolean; // Add this prop to disable form for finalized invoices
  draftInvoice?: any; // Draft invoice data for conversion
  isFromDraft?: boolean; // Flag to indicate if converting from draft
  duplicateInvoice?: any; // Duplicate invoice data
  isFromDuplicate?: boolean; // Flag to indicate if duplicating from existing invoice
  isInvoiceCreated?: boolean; // Flag to indicate if invoice was successfully created
}

export const SalesInvoiceForm: React.FC<SalesInvoiceFormProps> = ({
  salesInvoice,
  sites,
  godowns,
  voucherTypeId,
  isSubmitting,
  onSubmit,
  searchItems,
  isLoadingItems,
  isDisabled = false,
  draftInvoice,
  isFromDraft = false,
  duplicateInvoice,
  isFromDuplicate = false,
  isInvoiceCreated = false,
}) => {
  const isEditMode = !!salesInvoice; 
  const gridRef = useRef<HTMLDivElement>(null);
  
  // Get user preferences
  const { defaultSiteId, defaultGodownId: preferenceGodownId } = usePreferenceStore();
  
  // Default warehouse for all items
  const [defaultGodownId, setDefaultGodownId] = useState<number>(
    godowns.length > 0 ? (
      // Use preference godown if available, otherwise first godown
      preferenceGodownId && godowns.some(g => g.id === preferenceGodownId) 
        ? preferenceGodownId 
        : godowns[0].id
    ) : 0
  );
  
  // Prepare default values
  const defaultValues: Partial<SalesInvoiceFormValues> = {
    voucher_type_id: voucherTypeId,
    // Use preference site if available
    site_id: defaultSiteId && sites.some(s => s.id === defaultSiteId) ? defaultSiteId : undefined,
    transaction_date: new Date(),
    due_date: null,
    total_amount: 0,
    discount_type: null,
    discount_value: null,
    delivery_charge: null,
    items: [],
    // Add narration if converting from draft or duplicating
    narration: isFromDraft && draftInvoice 
      ? `Created from offline invoice #${draftInvoice.invoice_number || 'DRAFT'}`
      : isFromDuplicate && duplicateInvoice
      ? duplicateInvoice.narration || ''
      : '',
  };

  // Initialize form first to avoid reference errors
  const form = useForm<SalesInvoiceFormValues>({
    resolver: zodResolver(salesInvoiceSchema),
    defaultValues: salesInvoice
      ? {
          ...defaultValues,
          voucher_type_id: salesInvoice.voucher_type_id,
          party_id: salesInvoice.party_id || undefined,
          site_id: salesInvoice.site_id || undefined,
          transaction_date: salesInvoice.transaction_date
            ? new Date(salesInvoice.transaction_date)
            : new Date(),
          due_date: salesInvoice.due_date
            ? new Date(salesInvoice.due_date)
            : null,
          reference_number: salesInvoice.reference_number || '',
          narration: salesInvoice.narration || '',
          total_amount: parseFloat(String(salesInvoice.total_amount || 0)),
          discount_type: salesInvoice.discount_type || null,
          discount_value: salesInvoice.discount_value ? parseFloat(String(salesInvoice.discount_value)) : null,
          delivery_charge: salesInvoice.delivery_charge ? parseFloat(String(salesInvoice.delivery_charge)) : null,
          items: [],
        }
      : isFromDuplicate && duplicateInvoice
      ? {
          ...defaultValues,
          voucher_type_id: duplicateInvoice.voucher_type_id || voucherTypeId,
          party_id: duplicateInvoice.party_id || undefined,
          site_id: duplicateInvoice.site_id || undefined,
          transaction_date: duplicateInvoice.transaction_date || new Date(),
          due_date: duplicateInvoice.due_date || null,
          reference_number: duplicateInvoice.reference_number || '',
          narration: duplicateInvoice.narration || '',
          total_amount: parseFloat(String(duplicateInvoice.total_amount || 0)),
          discount_type: duplicateInvoice.discount_type || null,
          discount_value: duplicateInvoice.discount_value ? parseFloat(String(duplicateInvoice.discount_value)) : null,
          delivery_charge: duplicateInvoice.delivery_charge ? parseFloat(String(duplicateInvoice.delivery_charge)) : null,
          items: [],
        }
      : defaultValues,
  });

  // Add logging to debug user preferences after form is initialized
  useEffect(() => {
    console.log('[SalesInvoiceForm] User preferences loaded:', { 
      preferenceGodownId, 
      defaultSiteId,
      selectedGodownId: defaultGodownId,
      selectedSiteId: form.getValues('site_id')
    });
  }, [defaultSiteId, preferenceGodownId, defaultGodownId, form]);

  // Initialize items state for the grid
  const [lineItems, setLineItems] = useState<Record<string, unknown>[]>([]);

  // Register Cmd+S / Ctrl+S hotkey for form submission
  useFormHotkeys(() => {
    if (!isDisabled && !isSubmitting) {
      form.handleSubmit(handleSubmit)();
    }
  });

  // Initialize the line items for the grid AND update form state
  useEffect(() => {
    // Use draft invoice items if converting from draft, duplicate items if duplicating, otherwise use sales invoice items
    const apiItems = isFromDraft && draftInvoice?.items 
      ? draftInvoice.items 
      : isFromDuplicate && duplicateInvoice?.items
      ? duplicateInvoice.items
      : salesInvoice?.items || [];
    console.log('[Effect Init Items] API items to process:', apiItems);
    
    let itemsForGrid: Record<string, unknown>[] = [];
    let itemsForForm: SalesInvoiceFormValues['items'] = [];
    let calculatedTotal = 0;
    
    // For existing invoices in view/edit mode, use the actual total from backend
    if (isEditMode && salesInvoice?.total_amount) {
      calculatedTotal = Number(salesInvoice.total_amount);
    }

    // Define calculation helpers within the effect's scope
    const calculateDiscountAmount = (item: CalculationItem): number => {
        const quantity = parseFloat(String(item.quantity || 0));
        const rate = parseFloat(String(item.rate || 0));
        const discountPercent = parseFloat(String(item.discount_percent || 0));
        const grossAmount = quantity * rate;
        const discountAmount = (grossAmount * discountPercent) / 100;
        return Math.round(discountAmount * 100) / 100;
    };

    const calculateFinalAmount = (item: CalculationItem): number => {
        const quantity = parseFloat(String(item.quantity || 0));
        const rate = parseFloat(String(item.rate || 0));
        const discountAmount = calculateDiscountAmount(item);
        const grossAmount = quantity * rate;
        return Math.round((grossAmount - discountAmount) * 100) / 100;
    };

    if (apiItems && apiItems.length > 0) {
      try {
        // Sort items by item name (product name) by default
        const sortedApiItems = [...apiItems].sort((a, b) => {
          const nameA = (a.item?.name || a.description || '').toLowerCase();
          const nameB = (b.item?.name || b.description || '').toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
        
        itemsForGrid = sortedApiItems.map((item) => {
          const quantity = parseFloat(String(item.quantity || 0));
          const rate = parseFloat(String(item.rate || 0));
          const discountPercent = parseFloat(String(item.discount_percent || 0));
          
          // Use API-provided values for tax and totals if available
          const discountAmount = item.discount_amount ? parseFloat(String(item.discount_amount)) : calculateDiscountAmount({ quantity, rate, discount_percent: discountPercent });
          const taxableAmount = item.taxable_amount ? parseFloat(String(item.taxable_amount)) : calculateFinalAmount({ quantity, rate, discount_percent: discountPercent });
          const taxAmount = item.tax_amount ? parseFloat(String(item.tax_amount)) : 0;
          const totalAmount = item.total_amount ? parseFloat(String(item.total_amount)) : (taxableAmount + taxAmount);
          
          // Calculate tax rate percent from the tax amount and taxable amount if not provided
          let taxRatePercent = 0;
          let isTaxInclusive = false;
          
          // First check if we have tax rate info from the API
          if (item.tax_rate?.rate) {
            taxRatePercent = parseFloat(String(item.tax_rate.rate));
          } else if (taxAmount > 0 && taxableAmount > 0) {
            // If we have tax but no rate, calculate it
            // For tax-inclusive items, we need to calculate differently
            // But since we don't know if it's inclusive yet, assume exclusive
            taxRatePercent = (taxAmount / taxableAmount) * 100;
          }
          
          // For existing invoices, the is_tax_inclusive flag is critical
          // The API doesn't provide it, but we know from the screenshot that
          // items are tax-inclusive (rate includes tax)
          // We need to determine this from the item's behavior
          if (totalAmount && rate && quantity) {
            // If total = qty * rate, then it's tax-inclusive
            // If total = qty * rate + tax, then it's tax-exclusive
            const expectedInclusive = quantity * rate;
            const expectedExclusive = (quantity * rate) + taxAmount;
            
            // Check which calculation matches closer
            if (Math.abs(totalAmount - expectedInclusive) < 0.01) {
              isTaxInclusive = true;
            } else if (Math.abs(totalAmount - expectedExclusive) < 0.01) {
              isTaxInclusive = false;
            } else {
              // Default to true for Indian GST (typically inclusive)
              isTaxInclusive = true;
            }
          }
          
          return {
            id: item.id,
            item_id: item.item_id,
            item_name: item.item?.name || item.description || `Item ID: ${item.item_id}`,
            item_code: item.item?.code || '',
            description: item.description || '',
            quantity: quantity,
            rate: rate, // Keep the original rate without rounding
            discount_percent: discountPercent,
            discount_amount: discountAmount,
            tax_rate_id: item.tax_rate_id,
            taxable_amount: taxableAmount,
            tax_amount: taxAmount,
            amount: totalAmount,
            godown_id: item.godown_id || defaultGodownId,
            rate_decimal_places: item.item?.rate_decimal_places || 0, // Get from API response
            // Add missing tax-related fields that are needed for recalculation
            tax_rate_percent: taxRatePercent,
            is_tax_inclusive: isTaxInclusive,
          };
        });
        
        itemsForForm = itemsForGrid.map(item => ({
          id: typeof item.id === 'number' ? item.id : undefined,
          item_id: typeof item.item_id === 'number' ? item.item_id : 0,
          quantity: parseFloat(String(item.quantity || 0)),
          rate: parseFloat(String(item.rate || 0)),
          discount_percent: parseFloat(String(item.discount_percent || 0)),
          discount_amount: parseFloat(String(item.discount_amount || 0)),
          tax_rate_id: typeof item.tax_rate_id === 'number' ? item.tax_rate_id : null,
          amount: parseFloat(String(item.amount || 0)),
          description: String(item.description || ''),
          godown_id: typeof item.godown_id === 'number' ? item.godown_id : defaultGodownId,
        }));

        // Only calculate total for new invoices, for existing ones use the backend total
        if (!isEditMode || !salesInvoice?.total_amount) {
          calculatedTotal = Math.ceil(itemsForForm.reduce((sum, item) => sum + item.amount, 0));
        }

      } catch (error) {
        console.error('[Effect Init Items] Error formatting items:', error);
        itemsForGrid = [{ item_id: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, amount: 0, description: '', godown_id: defaultGodownId }];
        itemsForForm = [];
        calculatedTotal = 0;
      }
    } else {
      itemsForGrid = [{ item_id: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, amount: 0, description: '', godown_id: defaultGodownId }];
      itemsForForm = []; 
      calculatedTotal = 0;
    }

    setLineItems(itemsForGrid);
    form.setValue('items', itemsForForm);
    form.setValue('total_amount', calculatedTotal);

  }, [salesInvoice, isEditMode, defaultGodownId, form, draftInvoice, isFromDraft, duplicateInvoice, isFromDuplicate]); 
  
  // Update line items when default godown changes
  useEffect(() => {
    if (lineItems.length > 0) {
      setLineItems(currentItems => currentItems.map(item => {
        if (!item.godown_id || !item.item_id) {
          return { ...item, godown_id: defaultGodownId };
        }
        return item;
      }));
    }
  }, [defaultGodownId]); 
  
  // Update form state when lineItems change
  useEffect(() => {
    console.log('[SalesInvoiceForm] useEffect triggered - lineItems changed');
    console.log('[SalesInvoiceForm] Current lineItems:', JSON.stringify(lineItems.map((item, idx) => ({
      index: idx,
      quantity: item.quantity,
      rate: item.rate,
      taxable_amount: item.taxable_amount,
      tax_amount: item.tax_amount,
      amount: item.amount
    }))));
    
    // Skip initial run with placeholder data
    if (!salesInvoice && !isEditMode && !isFromDuplicate && lineItems.length === 1 && !lineItems[0].item_id) {
      console.log('[SalesInvoiceForm] Skipping initial placeholder row update for new invoice');
      return;
    }
    if (isEditMode && !salesInvoice?.items) {
      console.log('[SalesInvoiceForm] Skipping update in edit mode with no items');
      return;
    }
    
    console.log('[SalesInvoiceForm] Processing lineItems update');
    
    const filteredItems = lineItems.filter(item => {
      const hasItemId = item && (item.item_id || typeof item.item_id === 'number');
      return hasItemId;
    });
    
    console.log('[SalesInvoiceForm] Filtered items for form update:', filteredItems.length);
    
    // Update form items array for submission
    const formItems = filteredItems.map(item => {
      // Ensure all numeric values are properly parsed
      const quantity = parseFloat(String(item.quantity || 0));
      const rate = parseFloat(String(item.rate || 0));
      const discountPercent = parseFloat(String(item.discount_percent || 0));
      const discountAmount = parseFloat(String(item.discount_amount || 0));
      const amount = parseFloat(String(item.amount || 0));
      
      return {
        id: typeof item.id === 'number' ? item.id : undefined,
        item_id: typeof item.item_id === 'number' ? item.item_id : 0,
        quantity: isNaN(quantity) ? 0 : quantity,
        rate: isNaN(rate) ? 0 : rate,
        discount_percent: isNaN(discountPercent) ? 0 : discountPercent,
        discount_amount: isNaN(discountAmount) ? 0 : discountAmount,
        tax_rate_id: typeof item.tax_rate_id === 'number' ? item.tax_rate_id : null,
        amount: isNaN(amount) ? 0 : amount,
        description: String(item.description || ''),
        godown_id: typeof item.godown_id === 'number' ? item.godown_id : 
                parseInt(String(item.godown_id || defaultGodownId), 10) || defaultGodownId,
      };
    });
        
    // Recalculate total amount
    const total = formItems.reduce((sum, item) => {
        return sum + (item.amount || 0); 
    }, 0);
    // Round up to nearest whole number (e.g., 1234.33 becomes 1235)
    const roundedTotal = Math.ceil(total);
    
    console.log('[SalesInvoiceForm] Setting form values - total:', roundedTotal, 'items count:', formItems.length);

    form.setValue('items', formItems, { shouldValidate: false });
    form.setValue('total_amount', roundedTotal, { shouldValidate: false }); 

  }, [lineItems, defaultGodownId, form, isEditMode, salesInvoice, duplicateInvoice, isFromDuplicate]); 

  // Focus management for hotkeys
  useEffect(() => {
    const handleHotkey = (e: KeyboardEvent): void => {
      // Alt+N to focus on the grid (quick item addition)
      if (e.altKey && e.key === 'n') {
        e.preventDefault();
        if (gridRef.current) {
          gridRef.current.focus();
          // Attempt to focus the first product cell
          const firstProductCell = gridRef.current.querySelector('[role="button"]');
          if (firstProductCell instanceof HTMLElement) {
            firstProductCell.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleHotkey);
    return () => {
      document.removeEventListener('keydown', handleHotkey);
    };
  }, []);

  // Handle form submission
  const handleSubmit: SubmitHandler<SalesInvoiceFormValues> = (data) => {
    try {
      data.voucher_type_id = voucherTypeId;
      
      // Filter out items with missing product IDs or zero quantity
      const validItems = data.items.filter(item => 
        item.item_id && item.quantity > 0
      );
      
      if (validItems.length === 0) {
        toast.error("Cannot submit invoice with no valid items.");
        return;
      }
      
      // Calculate subtotal from items
      const itemsSubtotal = validItems.reduce((sum, item) => sum + item.amount, 0);
      
      // Calculate discount amount
      let discountAmount = 0;
      if (data.discount_type === 'percentage' && data.discount_value) {
        discountAmount = (itemsSubtotal * data.discount_value) / 100;
      } else if (data.discount_type === 'fixed' && data.discount_value) {
        discountAmount = data.discount_value;
      }
      
      // Calculate final total: Subtotal - Discount + Delivery Charge
      const netAmount = Math.max(0, itemsSubtotal - discountAmount);
      const deliveryCharge = data.delivery_charge || 0;
      const finalTotalAmount = netAmount + deliveryCharge;

      // Process each item to ensure required fields, including tax_rate_id
      const processedItems = validItems.map(item => {
        // Create a new object without the 'amount' field so backend calculates it
        const { amount, ...itemWithoutAmount } = item;
        
        // Tax issue tracking - processing items for backend
        console.log(`[TAX DEBUG] Processing item with tax_rate_id ${item.tax_rate_id} for backend calculation`);
        
        return {
          ...itemWithoutAmount,
          discount_percent: typeof item.discount_percent === 'number' ? item.discount_percent : 0,
          discount_amount: typeof item.discount_amount === 'number' ? item.discount_amount : 0,
          // Ensure tax_rate_id is included (already added in form state update)
        };
      });

      const formattedData = {
        ...data, 
        items: processedItems,
        transaction_date: format(data.transaction_date, 'yyyy-MM-dd'),
        due_date: data.due_date ? format(data.due_date, 'yyyy-MM-dd') : null,
        total_amount: Math.round(finalTotalAmount * 100) / 100, 
        is_cancelled: false,
        // Include discount fields
        discount_type: data.discount_type || null,
        discount_value: data.discount_value || null,
        // Include delivery charge
        delivery_charge: data.delivery_charge || null,
      };
      
      // Tax issue tracking - final data being submitted
      console.log(`[TAX DEBUG] Submit data:`, JSON.stringify(formattedData, null, 2));
      
      onSubmit(formattedData as unknown as SalesInvoiceFormValues);
    } catch (error) {
      toast.error(`Form submission error: ${(error as Error).message}`);
    }
  };

  // Wrapper for searchItems to convert Item[] to expected format
  const searchProductsWrapper = async (searchTerm: string): Promise<ProductSearchResult[]> => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    try {
      const items = await searchItems(searchTerm);
      return items.map(item => ({
        id: item.id,
        name: item.name || '',
        code: item.code || null, 
        description: item.description || item.name || null,
        tax_rate_id: item.tax_rate_id || null, // Include tax_rate_id for tax calculations
        rate_decimal_places: item.rate_decimal_places || 0, // Include rate_decimal_places for proper rate formatting
      }));
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  };

  // Handle product selection - Now fetches detailed pricing
  const handleProductSelect = async (product: ProductSearchResult, rowIndex: number): Promise<void> => {
    console.log(`[SalesInvoiceForm] handleProductSelect for row ${rowIndex}, item ID: ${product.id}`);
    
    // Get the current quantity for the row (default to 1 if not set)
    const currentRow = lineItems[rowIndex] || {};
    const currentQuantity = new Decimal(String(currentRow.quantity || 1));
    const currentSiteId = form.watch('site_id');
    const currentPartyId = form.watch('party_id'); // Get partyId for context
    
    // Tax issue tracking - product selection with tax_rate_id
    console.log(`[TAX DEBUG] Product selection with tax_rate_id: ${product.tax_rate_id}`);

    let priceDetails: any | null = null;
    try {
      // Tax issue tracking - calling price details with tax info
      console.log("[TAX DEBUG] Calling price details with tax_rate_id:", product.tax_rate_id);
      
      // Use centralized pricing function
      const pricingResult = await getItemPricingDetails(
        product.id,
        currentQuantity.toNumber(),
        currentSiteId,
        currentPartyId
      );
      
      if (pricingResult) {
        // Convert to the expected format for backward compatibility
        priceDetails = {
          rate: new Decimal(pricingResult.rate),
          discount_percent: new Decimal(pricingResult.discount_percent),
          discount_amount: new Decimal(pricingResult.discount_amount),
          tax_rate_id: pricingResult.tax_rate_id,
          tax_amount: new Decimal(pricingResult.tax_amount),
          tax_breakdown: pricingResult.tax_breakdown.map(tb => ({
            code: tb.code,
            rate: new Decimal(tb.rate),
            amount: new Decimal(tb.amount)
          })),
          final_amount: new Decimal(pricingResult.amount),
          taxable_amount: new Decimal(pricingResult.taxable_amount),
          applied_sale_rule_id: pricingResult.applied_sale_rule_id,
          is_tax_inclusive: pricingResult.is_tax_inclusive
        };
      }
      
      // Tax issue tracking - received price details
      console.log("[TAX DEBUG] Received priceDetails with tax info:", {
        tax_rate_id: priceDetails ? priceDetails.tax_rate_id : null,
        tax_amount: priceDetails ? priceDetails.tax_amount : null,
        final_amount: priceDetails ? priceDetails.final_amount : null,
        is_tax_inclusive: priceDetails ? priceDetails.is_tax_inclusive : null,
        rate: priceDetails ? priceDetails.rate.toString() : null,
        taxable_amount: priceDetails ? priceDetails.taxable_amount.toString() : null
      });
      
    } catch (error) {
      console.error("[SalesInvoiceForm] Error fetching final price details:", error);
      toast.error(`Error calculating price for ${product.name}: ${(error as Error).message}`);
      priceDetails = null;
    }

    // Update the grid data state
    setLineItems(currentItems => {
      const newItems = [...currentItems];
      if (newItems[rowIndex]) {
        // Start with existing row data and basic product info
        const rate = priceDetails ? priceDetails.rate.toNumber() : 0;
        const updatedRow = {
          ...newItems[rowIndex],
          item_id: product.id,
          item_name: product.name,
          item_code: product.code || '',
          description: '',
          godown_id: newItems[rowIndex].godown_id || defaultGodownId,
          quantity: currentQuantity.toNumber(),
          // Apply fetched price details with rate (rounding handled by utils)
          rate: rate,
          discount_percent: priceDetails ? priceDetails.discount_percent.toNumber() : 0,
          discount_amount: priceDetails ? priceDetails.discount_amount.toNumber() : 0,
          // Calculate taxable amount
          taxable_amount: priceDetails ? priceDetails.taxable_amount.toNumber() : 0,
          tax_rate_id: priceDetails ? priceDetails.tax_rate_id : null,
          tax_amount: priceDetails ? priceDetails.tax_amount.toNumber() : 0,
          amount: priceDetails ? priceDetails.final_amount.toNumber() : 0,
          applied_sale_rule_id: priceDetails ? priceDetails.applied_sale_rule_id : null, // Store applied rule ID
          is_tax_inclusive: priceDetails ? priceDetails.is_tax_inclusive : false, // Store tax-inclusive flag
          // Calculate and store total tax rate percentage for synchronous calculations
          tax_rate_percent: priceDetails && priceDetails.tax_breakdown 
            ? priceDetails.tax_breakdown.reduce((sum: number, component: any) => 
                sum + component.rate.toNumber(), 0)
            : 0,
          rate_decimal_places: product.rate_decimal_places || 0, // Store the decimal places configuration
        };

        // Tax issue tracking - row update with tax info
        console.log(`[TAX DEBUG] Updated row ${rowIndex} tax data:`, {
          tax_rate_id: updatedRow.tax_rate_id,
          taxable_amount: updatedRow.taxable_amount,
          tax_amount: updatedRow.tax_amount,
          final_amount: updatedRow.amount,
          rate: updatedRow.rate,
          rate_original: priceDetails ? priceDetails.rate.toNumber() : null,
          is_tax_inclusive: updatedRow.is_tax_inclusive,
          raw_values: priceDetails ? {
            taxable_amount_raw: priceDetails.taxable_amount.toString(),
            amount_raw: priceDetails.final_amount.toString(),
            tax_amount_raw: priceDetails.tax_amount.toString()
          } : null
        });

        newItems[rowIndex] = updatedRow;
      }
      return newItems;
    });

    // Focus is handled by EditableDataGrid's handleSelectItem after state update
  };

  // Handle quantity change for a row with applied sale rule
  const handleQuantityChangeForRow = async (rowIndex: number, newQuantity: number): Promise<void> => {
    // Tax issue tracking - quantity change with potential tax implications
    console.log(`[TAX DEBUG] Quantity changed for row ${rowIndex} to ${newQuantity}. Re-evaluating price with tax.`);

    const itemInRow = lineItems[rowIndex];
    if (!itemInRow) {
      console.warn(`[SalesInvoiceForm] Can't update quantity: No item found at row ${rowIndex}`);
      return;
    }

    const itemId = Number(itemInRow.item_id);
    if (!itemId) {
      console.warn(`[SalesInvoiceForm] Can't update quantity: No valid item_id in row ${rowIndex}`);
      return;
    }

    const currentSiteId = form.watch('site_id');
    const currentPartyId = form.watch('party_id');
    // Get current tax_rate_id to ensure it's used in the calculation
    const currentTaxRateId = Number(itemInRow.tax_rate_id) || null;
    const currentRateDecimalPlaces = Number(itemInRow.rate_decimal_places) || 0;
    const hasAppliedRule = Boolean(itemInRow.applied_sale_rule_id);
    const currentDiscountPercent = parseFloat(String(itemInRow.discount_percent || 0));
    const currentDiscountAmount = parseFloat(String(itemInRow.discount_amount || 0));

    try {
      console.log(`[SalesInvoiceForm] Recalculating price for item ${itemId} with quantity ${newQuantity}, has rule: ${hasAppliedRule}`);

      // Use centralized pricing function for quantity change
      const pricingResult = await getItemPricingDetails(
        itemId,
        newQuantity,
        currentSiteId,
        currentPartyId,
        currentDiscountPercent,
        currentDiscountAmount
      );
      
      let priceDetails = null;
      if (pricingResult) {
        // Convert to the expected format for backward compatibility
        priceDetails = {
          rate: new Decimal(pricingResult.rate),
          discount_percent: new Decimal(pricingResult.discount_percent),
          discount_amount: new Decimal(pricingResult.discount_amount),
          tax_rate_id: pricingResult.tax_rate_id,
          tax_amount: new Decimal(pricingResult.tax_amount),
          tax_breakdown: pricingResult.tax_breakdown.map(tb => ({
            code: tb.code,
            rate: new Decimal(tb.rate),
            amount: new Decimal(tb.amount)
          })),
          final_amount: new Decimal(pricingResult.amount),
          taxable_amount: new Decimal(pricingResult.taxable_amount),
          applied_sale_rule_id: pricingResult.applied_sale_rule_id,
          is_tax_inclusive: pricingResult.is_tax_inclusive,
          source: 'centralized'
        };
      }

      if (!priceDetails) {
        console.error(`[SalesInvoiceForm] Failed to get price details for item ${itemId}`);
        toast.error(`Failed to recalculate price for item.`);
        return;
      }

      // Tax issue tracking - received updated price details after quantity change
      console.log(`[TAX DEBUG] Re-calculated price details with tax for item ${itemId}:`, {
        rate: priceDetails.rate.toNumber(),
        quantity_used: newQuantity,
        taxable_amount: priceDetails.taxable_amount.toNumber(),
        tax_rate_id: priceDetails.tax_rate_id,
        tax_amount: priceDetails.tax_amount.toNumber(),
        final_amount: priceDetails.final_amount.toNumber(),
        source: priceDetails.source,
        applied_rule_id: priceDetails.applied_sale_rule_id
      });

      setLineItems(currentItems => {
        const newItems = [...currentItems];
        if (newItems[rowIndex]) {
          // Get current values
          const currentDiscountPercent = parseFloat(String(newItems[rowIndex].discount_percent || 0));
          const currentDiscountAmount = parseFloat(String(newItems[rowIndex].discount_amount || 0));
          const currentQuantity = parseFloat(String(newItems[rowIndex].quantity || 0));
          
          // Check if the quantity matches what we requested (to avoid race conditions)
          if (Math.abs(currentQuantity - newQuantity) > 0.01) {
            console.log(`[SalesInvoiceForm] Quantity mismatch - current: ${currentQuantity}, requested: ${newQuantity}. Skipping update.`);
            return currentItems;
          }
          
          // Check if user has manually entered discounts
          const hasManualDiscount = currentDiscountPercent > 0 || currentDiscountAmount > 0;
          
          // If user has manual discounts, recalculate with them instead of API values
          if (hasManualDiscount) {
            const updatedRow = {
              ...newItems[rowIndex],
              rate: priceDetails.rate.toNumber(),
              tax_rate_id: priceDetails.tax_rate_id,
              applied_sale_rule_id: priceDetails.applied_sale_rule_id,
              is_tax_inclusive: priceDetails.is_tax_inclusive || false,
              // Keep user's discount values
              discount_percent: currentDiscountPercent,
              discount_amount: currentDiscountAmount,
            };
            
            // Calculate total tax rate from tax breakdown
            const totalTaxRate = priceDetails.tax_breakdown.reduce((sum, component) => 
              sum + component.rate.toNumber(), 0
            );
            
            // Recalculate amounts synchronously
            const grossAmount = priceDetails.rate.toNumber() * newQuantity;
            const discountAmount = currentDiscountPercent > 0 
              ? (grossAmount * currentDiscountPercent) / 100 
              : currentDiscountAmount;
            
            let taxableAmount, taxAmount, totalAmount;
            
            if (priceDetails.is_tax_inclusive) {
              // Tax-inclusive: total is gross - discount, tax is extracted
              totalAmount = grossAmount - discountAmount;
              if (totalTaxRate > 0) {
                taxAmount = (totalAmount * totalTaxRate) / (100 + totalTaxRate);
                taxableAmount = totalAmount - taxAmount;
              } else {
                taxAmount = 0;
                taxableAmount = totalAmount;
              }
            } else {
              // Tax-exclusive: tax is added on top
              taxableAmount = grossAmount - discountAmount;
              taxAmount = totalTaxRate > 0 ? (taxableAmount * totalTaxRate) / 100 : 0;
              totalAmount = taxableAmount + taxAmount;
            }
            
            newItems[rowIndex] = {
              ...updatedRow,
              taxable_amount: taxableAmount,
              tax_amount: taxAmount,
              amount: totalAmount,
              // Store tax rate percentage for rate change calculations
              tax_rate_percent: totalTaxRate
            };
            
            console.log(`[SalesInvoiceForm] Updated row ${rowIndex} with manual discount - amount: ${totalAmount}`);
          } else {
            // No manual discount, use API values
            // Note: API returns per-unit values, we need to multiply by quantity for totals
            const unitRate = priceDetails.rate.toNumber();
            const unitTaxAmount = priceDetails.tax_amount.toNumber();
            
            // Recalculate all amounts based on rate
            const discountPercent = priceDetails.discount_percent.toNumber();
            const grossAmount = unitRate * newQuantity;
            const discountAmount = discountPercent > 0 ? (grossAmount * discountPercent) / 100 : 0;
            const isTaxInclusive = priceDetails.is_tax_inclusive || false;
            
            let taxableAmount, taxAmount, totalAmount;
            if (isTaxInclusive) {
              // For tax-inclusive: total is the gross amount, tax is extracted from it
              totalAmount = grossAmount - discountAmount;
              // If we have tax rate, calculate backwards
              if (priceDetails.tax_rate_id && priceDetails.tax_breakdown) {
                const totalTaxRate = priceDetails.tax_breakdown.reduce((sum: number, component: any) => 
                  sum + component.rate.toNumber(), 0);
                if (totalTaxRate > 0) {
                  taxAmount = (totalAmount * totalTaxRate) / (100 + totalTaxRate);
                  taxableAmount = totalAmount - taxAmount;
                } else {
                  taxAmount = 0;
                  taxableAmount = totalAmount;
                }
              } else {
                taxAmount = unitTaxAmount * newQuantity;
                taxableAmount = totalAmount - taxAmount;
              }
            } else {
              // For tax-exclusive: tax is added on top
              taxableAmount = grossAmount - discountAmount;
              taxAmount = unitTaxAmount * newQuantity;
              totalAmount = taxableAmount + taxAmount;
            }
            
            newItems[rowIndex] = {
              ...newItems[rowIndex],
              rate: unitRate,
              discount_percent: discountPercent,
              discount_amount: discountAmount,
              taxable_amount: taxableAmount,
              tax_rate_id: priceDetails.tax_rate_id,
              tax_amount: taxAmount,
              amount: totalAmount,
              applied_sale_rule_id: priceDetails.applied_sale_rule_id,
              is_tax_inclusive: priceDetails.is_tax_inclusive || false,
              // Store tax rate percentage for rate change calculations
              tax_rate_percent: priceDetails && priceDetails.tax_breakdown 
                ? priceDetails.tax_breakdown.reduce((sum: number, component: any) => 
                    sum + component.rate.toNumber(), 0)
                : 0
            };
            
            console.log(`[SalesInvoiceForm] Updated row ${rowIndex} with API values:`, {
              rate: unitRate,
              taxable_amount: taxableAmount,
              tax_amount: taxAmount,
              amount: totalAmount
            });
          }
        }
        return newItems;
      });
    } catch (error) {
      console.error(`[SalesInvoiceForm] Error re-fetching price details for item ${itemId} on quantity change:`, error);
      toast.error(`Error recalculating price: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Column definition - update to include tax fields
  const columns: GridColumn[] = useMemo(() => [
    { field: 'item_id', header: 'Product', type: 'product', width: '200px', sortable: true },
    { field: 'quantity', header: 'Qty', type: 'number', width: '80px', sortable: true },
    { field: 'rate', header: 'Rate', type: 'price', width: '100px', sortable: true },
    { field: 'discount_percent', header: 'Disc %', type: 'number', width: '80px', sortable: false },
    { field: 'discount_amount', header: 'Disc Amt', type: 'price', width: '100px', sortable: false },
    // Read-only calculated fields populated by the pricing util
    { field: 'taxable_amount', header: 'Taxable Amt', type: 'price', editable: false, width: '120px', sortable: false },
    { field: 'tax_amount', header: 'Tax Amt', type: 'price', editable: false, width: '100px', sortable: false },
    { field: 'amount', header: 'Amount', type: 'price', editable: false, width: '120px', sortable: true },
    { field: 'description', header: 'Description', type: 'text', width: '180px', sortable: false },
    { field: 'actions', header: '', type: 'actions', width: '50px', sortable: false },
  ], [isEditMode]); // Add isEditMode dependency

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Party and Date Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Customer Selection - Replaced with PartySelector */}
          <FormField
            control={form.control}
            name="party_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Customer *</FormLabel>
                <PartySelector
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  placeholder="Select or create a customer..."
                  filterPartyType="customer"
                  disabled={isSubmitting || isDisabled}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Site Selection */}
          <FormField
            control={form.control}
            name="site_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Site</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(parseInt(value))}
                  value={field.value?.toString()}
                  disabled={isSubmitting || isDisabled}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a site" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id.toString()}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Transaction Date */}
          <FormField
            control={form.control}
            name="transaction_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isSubmitting || isDisabled}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                      disabled={isDisabled}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Due Date */}
          <FormField
            control={form.control}
            name="due_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Due Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isSubmitting || isDisabled}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Select due date (optional)</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value || undefined}
                      onSelect={field.onChange}
                      initialFocus
                      disabled={isDisabled}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Reference Number */}
          <FormField
            control={form.control}
            name="reference_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reference Number</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Enter reference number (optional)"
                    value={field.value || ''}
                    disabled={isSubmitting || isDisabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {/* Warehouse Selection (Default for all items) */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Warehouse</label>
            <Select
              onValueChange={(value) => setDefaultGodownId(parseInt(value))}
              value={defaultGodownId.toString()}
              disabled={isSubmitting || isDisabled}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select warehouse" />
              </SelectTrigger>
              <SelectContent>
                {godowns.map((godown) => (
                  <SelectItem key={godown.id} value={godown.id.toString()}>
                    {godown.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">This warehouse will be used for all items</p>
          </div>
        </div>

        {/* Narration / Notes */}
        <FormField
          control={form.control}
          name="narration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Enter notes or comments (optional)"
                  value={field.value || ''}
                  rows={3}
                  disabled={isSubmitting || isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Line Items */}
        <Card ref={gridRef} tabIndex={-1}>
          <CardContent className="pt-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Items</h3>
              <div className="text-sm text-muted-foreground">
                Press <kbd className="px-1 py-0.5 border rounded text-xs">Alt+N</kbd> to focus on items | 
                <kbd className="px-1 py-0.5 border rounded text-xs mx-1">Tab</kbd> to navigate | 
                <kbd className="px-1 py-0.5 border rounded text-xs">Enter</kbd> to select
              </div>
            </div>
            <EditableDataGrid
              columns={columns}
              data={lineItems}
              setData={setLineItems}
              productSearchFn={searchProductsWrapper}
              isLoadingProducts={isLoadingItems}
              disabled={isSubmitting || isDisabled}
              onProductSelect={handleProductSelect}
              siteId={form.watch('site_id')}
              onQuantityChangeForRow={handleQuantityChangeForRow}
            />
          </CardContent>
        </Card>

        {/* Discount Section */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Discount & Total</h3>
              
              {/* Subtotal (Items + Tax) */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Subtotal (Items + Tax):</span>
                <span className="font-medium">
                  {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                  }).format(
                    lineItems.reduce((sum, item) => sum + (parseFloat(String(item.amount || 0))), 0)
                  )}
                </span>
              </div>

              {/* Discount Type and Value */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="discount_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Discount Type</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value === 'none' ? null : value);
                          // Clear discount value when type changes
                          form.setValue('discount_value', null);
                        }}
                        value={field.value || 'none'}
                        disabled={isSubmitting || isDisabled}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="No discount" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No discount</SelectItem>
                          <SelectItem value="percentage">Percentage (%)</SelectItem>
                          <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch('discount_type') && form.watch('discount_type') !== 'none' && (
                  <FormField
                    control={form.control}
                    name="discount_value"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Discount {form.watch('discount_type') === 'percentage' ? 'Percentage' : 'Amount'}
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type="number"
                              step="0.01"
                              min="0"
                              max={form.watch('discount_type') === 'percentage' ? 100 : undefined}
                              placeholder={form.watch('discount_type') === 'percentage' ? "0.00" : "0.00"}
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={isSubmitting || isDisabled}
                              className={form.watch('discount_type') === 'percentage' ? "pr-8" : ""}
                            />
                            {form.watch('discount_type') === 'percentage' && (
                              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
                                %
                              </span>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Calculated Discount Amount */}
                {form.watch('discount_type') && form.watch('discount_type') !== 'none' && form.watch('discount_value') && (
                  <div className="flex flex-col space-y-2">
                    <FormLabel>Discount Amount</FormLabel>
                    <div className="text-lg font-medium">
                      {(() => {
                        const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(String(item.amount || 0))), 0);
                        const discountType = form.watch('discount_type');
                        const discountValue = form.watch('discount_value') || 0;
                        
                        let discountAmount = 0;
                        if (discountType === 'percentage') {
                          discountAmount = (subtotal * discountValue) / 100;
                        } else {
                          discountAmount = discountValue;
                        }
                        
                        return new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                        }).format(discountAmount);
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Delivery Charge */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="delivery_charge"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Charge</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          disabled={isSubmitting || isDisabled}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {form.watch('delivery_charge') && (
                  <div className="flex flex-col space-y-2">
                    <FormLabel>&nbsp;</FormLabel>
                    <div className="text-lg font-medium">
                      {new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                      }).format(form.watch('delivery_charge') || 0)}
                    </div>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Amount:</span>
                  <span className="text-2xl font-bold">
                    {(() => {
                      const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(String(item.amount || 0))), 0);
                      const discountType = form.watch('discount_type');
                      const discountValue = form.watch('discount_value') || 0;
                      const deliveryCharge = form.watch('delivery_charge') || 0;
                      
                      let discountAmount = 0;
                      if (discountType === 'percentage') {
                        discountAmount = (subtotal * discountValue) / 100;
                      } else if (discountType === 'fixed') {
                        discountAmount = discountValue;
                      }
                      
                      // Calculate final total: Subtotal - Discount + Delivery Charge
                      const netAmount = Math.max(0, subtotal - discountAmount);
                      const finalTotal = netAmount + deliveryCharge;
                      
                      // Update form's total_amount
                      if (form.getValues('total_amount') !== finalTotal) {
                        form.setValue('total_amount', finalTotal, { shouldValidate: false });
                      }
                      
                      return new Intl.NumberFormat('en-IN', {
                        style: 'currency',
                        currency: 'INR',
                      }).format(finalTotal);
                    })()}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <div className="hidden">{/* Keep old total display hidden for compatibility */}
            Total: {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
            }).format(form.watch('total_amount') || 0)}
          </div>
          <Button 
            type="submit" 
            disabled={isSubmitting || isDisabled || isInvoiceCreated}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {salesInvoice ? 'Updating...' : 'Creating...'}
              </>
            ) : isInvoiceCreated ? (
              <>Invoice Created Successfully</>
            ) : (
              <>{salesInvoice ? 'Update' : 'Create'} Sales Invoice</>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};